import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import css from "./Dropdown.module.css";

/**
 * Dropdown — React-порт design-references/dropdown.js (A34, M.4): единый
 * выпадающий список, кнопок «Сохранить/Готово» НЕТ. Управляемый компонент
 * в стиле TimePicker/NumPad; синглтон эталона = один <Dropdown>-хост на
 * экран, opts через state. Тап по пункту = onCommit([id]) + закрытие.
 * Long-press 480мс (движение >8px отменяет) → мультирежим: чекбоксы,
 * счётчик «Выбрано: N», тапы toggle, тап МИМО = применить всё, Esc =
 * отмена без применения; single: тап мимо/Esc = закрытие без commit.
 * Contextmenu подавлен; фильтр при items>8 (или проп filter). place()
 * эталона (dd:67-80): вниз → вверх → большая сторона со сжатием списка,
 * клампы 6px, потолок 40dvh; скролл вне поповера = семантика «тапа мимо»
 * (спека §2 + A34): multi применяет набранный выбор, single — без commit
 * (resize не слушаем — клавиатура фильтра ресайзит вьюпорт TMA).
 * Отличия: onCommit ВСЕГДА массив (эталон в single отдаёт скаляр, API —
 * по спеке dropdown-module-prompt.md); onClose зовётся ПЕРЕД onCommit
 * (хост в commit может тут же открыть следующий поповер/NumPad).
 */
export type DropdownId = string | number;

export interface DropdownItem {
  id: DropdownId;
  label: string;
  /** Мета справа у пункта (единица измерения, счётчик). */
  meta?: string;
  /** Приглушён и невыбираем (например, уже добавленные позиции). */
  disabled?: boolean;
}

export interface DropdownProps {
  open: boolean;
  /** Якорь позиционирования (обычно ev.currentTarget кнопки «+»). */
  anchor: HTMLElement | null;
  items: DropdownItem[];
  /** Подсветка текущего выбора; стартовый набор мультирежима. */
  selectedIds?: DropdownId[];
  /** false — long-press-мультивыбор запрещён (по умолчанию разрешён). */
  multi?: boolean;
  /** 31.08 (владелец): пункт-действие ПЕРВЫМ в списке (например «+ новый
      расходник…») — тап закрывает дропдаун и зовёт onPress. */
  action?: { label: string; onPress: () => void };
  /** 28.08 (владелец): открывать СРАЗУ в мультирежиме (привязки в справочниках). */
  multiDefault?: boolean;
  /** true — фильтр всегда, false — никогда; не задан — авто при >8. */
  filter?: boolean;
  /** Шапка в single-режиме (в multi её место занимает счётчик). */
  title?: string;
  /** single: [id]; multi: все выбранные. Зовётся ПОСЛЕ onClose. */
  onCommit: (ids: DropdownId[]) => void;
  /** Закрытие без commit: Esc, тап мимо/скролл хоста в single. */
  onClose: () => void;
  /** Выше экранных модалок, ниже TimePicker (+40) и NumPad (+60). */
  zIndex?: number | string;
}

const LP_MS = 480;
const LP_MOVE = 8;

/* Чекбокс мультирежима (эталон CB_ON/CB_OFF, dd:34-35). */
function CheckSvg({ on }: { on: boolean }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" opacity={on ? 1 : 0.45}>
      <rect x="3.5" y="3.5" width="17" height="17" />
      {on ? <path d="M7.5 12.5l3.5 3.5 6-7" strokeWidth="2.4" /> : null}
    </svg>
  );
}

export function Dropdown({
  open,
  anchor,
  items,
  selectedIds,
  multi = true,
  multiDefault = false,
  action,
  filter,
  title,
  onCommit,
  onClose,
  zIndex = "calc(var(--z-modal) + 20)",
}: DropdownProps) {
  const [sel, setSel] = useState<Set<DropdownId>>(() => new Set());
  const [multiOn, setMultiOn] = useState(false);
  const [q, setQ] = useState("");
  const [entered, setEntered] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lpT = useRef(0);
  const lpXY = useRef({ x: 0, y: 0 });
  const longRef = useRef(false);
  const stRef = useRef({ multiOn, sel, onCommit, onClose });
  stRef.current = { multiOn, sel, onCommit, onClose };
  const selectedRef = useRef(selectedIds);
  selectedRef.current = selectedIds;

  /* Сброс на открытие/смену якоря — эталонный open() строит всё заново. */
  useEffect(() => {
    if (!open) return;
    setSel(new Set(selectedRef.current ?? []));
    /* 28.08 (владелец): привязки открываются СРАЗУ мультивыбором */
    setMultiOn(multi && multiDefault);
    setQ("");
  }, [open, anchor, multi, multiDefault]);

  /* place() эталона (dd:67-80): координаты вьюпорта (портал в body). */
  useLayoutEffect(() => {
    if (!open) return;
    const pop = popRef.current;
    const list = listRef.current;
    if (!pop || !list || !anchor) return;
    list.style.maxHeight = "";
    const ar = anchor.getBoundingClientRect();
    const ph = pop.offsetHeight;
    const pw = pop.offsetWidth;
    const left = Math.max(6, Math.min(ar.left, window.innerWidth - pw - 6));
    const spaceDown = window.innerHeight - ar.bottom - 8;
    const spaceUp = ar.top - 8;
    let top: number;
    if (ph <= spaceDown) top = ar.bottom + 4;
    else if (ph <= spaceUp) top = ar.top - ph - 4;
    else if (spaceDown >= spaceUp) {
      top = ar.bottom + 4;
      list.style.maxHeight = `${Math.max(64, spaceDown - 40)}px`;
    } else {
      list.style.maxHeight = `${Math.max(64, spaceUp - 40)}px`;
      top = Math.max(6, ar.top - pop.offsetHeight - 4);
    }
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }, [open, anchor]);

  /* Появление 130мс — класс .on через rAF (эталон dd:92). */
  useEffect(() => {
    if (!open) return setEntered(false);
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  /* Тап мимо (setTimeout(0) пропускает открывший тап — dd:93-95), Esc
     capture (dd:42); скролл вне поповера = «тап мимо»: multi обязан
     применить набранное (A34), молчаливая потеря выбора недопустима. */
  useEffect(() => {
    if (!open) return;
    const inPop = (e: Event) => e.target instanceof Node && popRef.current?.contains(e.target);
    const dismiss = () => {
      const s = stRef.current;
      s.onClose();
      if (s.multiOn) s.onCommit([...s.sel]);
    };
    const onDown = (e: globalThis.PointerEvent) => { if (!inPop(e)) dismiss(); };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      stRef.current.onClose();
    };
    const onScroll = (e: Event) => { if (!inPop(e)) dismiss(); };
    const t = window.setTimeout(() => document.addEventListener("pointerdown", onDown, true), 0);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  const lpCancel = () => window.clearTimeout(lpT.current);

  const itemDown = (it: DropdownItem, e: ReactPointerEvent) => {
    longRef.current = false;
    if (it.disabled || !multi || multiOn) return;
    lpXY.current = { x: e.clientX, y: e.clientY };
    lpT.current = window.setTimeout(() => {
      longRef.current = true;
      setMultiOn(true);
      setSel((p) => new Set(p).add(it.id));
    }, LP_MS);
  };

  const itemMove = (e: ReactPointerEvent) => {
    if (Math.abs(e.clientX - lpXY.current.x) > LP_MOVE || Math.abs(e.clientY - lpXY.current.y) > LP_MOVE) lpCancel();
  };

  const itemClick = (it: DropdownItem) => {
    if (it.disabled) return;
    if (longRef.current) {
      longRef.current = false; // long-press глушит последующий click (dd:63)
      return;
    }
    if (multiOn) {
      setSel((p) => {
        const n = new Set(p);
        if (!n.delete(it.id)) n.add(it.id);
        return n;
      });
    } else {
      onClose();
      onCommit([it.id]);
    }
  };

  const ql = q.toLowerCase();
  const useFilter = filter === true || (filter !== false && items.length > 8);
  const shown = ql ? items.filter((it) => it.label.toLowerCase().includes(ql)) : items;

  if (!open || !anchor) return null;
  return createPortal(
    <div className={css.host} style={{ zIndex }}>
      <div ref={popRef} className={`${css.pop} ${multiOn ? css.multi : ""} ${entered ? css.on : ""}`}>
        {multiOn ? (
          <div className={css.head}>
            Выбрано: {sel.size}
            {/* 28.08 (владелец): явная кнопка вместо «догадайся про long-press» */}
            <button type="button" className={css.headBtn}
              onClick={() => { const ids = [...sel]; onClose(); if (ids.length) onCommit(ids); }}>готово</button>
          </div>
        ) : (
          <div className={css.head}>
            {title ?? ""}
            {multi ? <button type="button" className={css.headBtn} onClick={() => setMultiOn(true)}>выбрать несколько</button> : null}
          </div>
        )}
        {useFilter ? (
          <div className={css.filter}>
            <input type="text" placeholder="Фильтр…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        ) : null}
        <div ref={listRef} className={css.list} role="listbox" aria-multiselectable={multiOn}>
          {action ? (
            <button type="button" className={`${css.item} ${css.actionItem}`}
              onClick={() => { onClose(); action.onPress(); }}>
              <span className={css.lb}>{action.label}</span>
            </button>
          ) : null}
          {shown.length === 0 ? (
            <div className={css.empty}>Ничего не найдено</div>
          ) : (
            shown.map((it) => (
              <button
                key={it.id}
                type="button"
                role="option"
                aria-selected={sel.has(it.id)}
                aria-disabled={it.disabled || undefined}
                className={`${css.it} ${sel.has(it.id) ? css.sel : ""} ${it.disabled ? css.dis : ""}`}
                onPointerDown={(e) => itemDown(it, e)}
                onPointerMove={itemMove}
                onPointerUp={lpCancel}
                onPointerLeave={lpCancel}
                onPointerCancel={lpCancel}
                onContextMenu={(e) => e.preventDefault()}
                onClick={() => itemClick(it)}
              >
                <span className={css.cb}>
                  <CheckSvg on={sel.has(it.id)} />
                </span>
                <span className={css.lb}>{it.label}</span>
                {it.meta ? <span className={css.mt}>{it.meta}</span> : null}
              </button>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
